import { IterableMap } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import FilterLinesIcon from "@redotech/redo-icons/filter-lines_filled.svg";
import { memo } from "react";
import {
  AdvancedTableFilter,
  encodeAdvancedFilters,
} from "../advanced-filters/advanced-filter";
import { RedoButton } from "../arbiter-components/buttons/redo-button";
import { AllFiltersModal } from "./all-filters-modal";
import { FilterChipsList, FilterComponent } from "./filter-chips-list";

interface TableFiltersModalProps {
  "data-dd-action-name"?: string;
  open: boolean;
  onClose: () => void;
  shouldShowFilterButton: boolean;
  shouldShowAdvancedFilterButton: boolean;
  filterComponents: FilterComponent[];
  selectedFilters: string[];
  filters: Record<string, string>;
  onFiltersChange: (filters: Record<string, string>) => void;
  onSelectedFiltersChange: (selectedFilters: string[]) => void;
  displayedAdvancedFilters: AdvancedTableFilter[];
  onAdvancedFiltersChange: (params: string) => void;
  allFiltersMatch: boolean;
  onResetToViewFilters: () => void;
  showResetFilters?: boolean;
  onAddFilterClick: () => void;
  addFilterButtonRef: ((ref: HTMLButtonElement | null) => void) | undefined;
  AdvancedFilterComponent: React.ComponentType<{
    filter: AdvancedTableFilter;
    filters: AdvancedTableFilter[];
    isViewFilter: boolean;
    removeFilter: () => void;
    setValue: (filter: AdvancedTableFilter) => void;
  }>;
}

export const TableFiltersModal = memo(function TableFiltersModal({
  open,
  onClose,
  shouldShowFilterButton,
  shouldShowAdvancedFilterButton,
  filterComponents,
  selectedFilters,
  filters,
  onFiltersChange,
  onSelectedFiltersChange,
  displayedAdvancedFilters,
  onAdvancedFiltersChange,
  allFiltersMatch,
  onResetToViewFilters,
  showResetFilters = true,
  onAddFilterClick,
  addFilterButtonRef,
  AdvancedFilterComponent,
  "data-dd-action-name": actionName,
}: TableFiltersModalProps) {
  const { t } = useTranslation();
  const handleClose = useHandler(() => {
    onClose();
  });

  const handleResetFilters = useHandler(() => {
    onResetToViewFilters();
  });

  return (
    <AllFiltersModal
      addFilterButton={
        shouldShowAdvancedFilterButton ? (
          <RedoButton
            data-dd-action-name={actionName}
            hierarchy="primary"
            IconLeading={FilterLinesIcon}
            onClick={onAddFilterClick}
            ref={addFilterButtonRef}
            text={t("Add filter", { context: "filters" })}
          />
        ) : undefined
      }
      data-dd-action-name={actionName}
      onClose={handleClose}
      onResetFilters={
        showResetFilters && shouldShowAdvancedFilterButton && !allFiltersMatch
          ? handleResetFilters
          : undefined
      }
      open={open}
    >
      {shouldShowFilterButton && (
        <FilterChipsList
          data-dd-action-name={actionName}
          filterComponents={filterComponents}
          filters={filters}
          onFiltersChange={onFiltersChange}
          onSelectedFiltersChange={onSelectedFiltersChange}
          selectedFilters={selectedFilters}
        />
      )}
      {shouldShowAdvancedFilterButton && (
        <IterableMap
          items={displayedAdvancedFilters}
          keyFn={(filter, index) => filter.data.name + index}
        >
          {(filter, index) => {
            function setValue(newFilter: AdvancedTableFilter) {
              const newFilters = displayedAdvancedFilters.map((f, i) =>
                index === i ? newFilter : f,
              );
              onAdvancedFiltersChange(encodeAdvancedFilters(newFilters));
            }

            function removeFilter() {
              const newFilters = displayedAdvancedFilters.filter(
                (_, i) => i !== index,
              );
              onAdvancedFiltersChange(encodeAdvancedFilters(newFilters));
            }

            return (
              <AdvancedFilterComponent
                filter={filter}
                filters={displayedAdvancedFilters}
                isViewFilter
                removeFilter={removeFilter}
                setValue={setValue}
              />
            );
          }}
        </IterableMap>
      )}
    </AllFiltersModal>
  );
});
